<template>
  <div class="tcla-graph">
    <div class="tcla-panel">
      <div class="tcla-mode-switch">
        <button class="tcla-mode-btn" :class="{ 'tcla-mode-btn--active': mode === 'forest' }" @click="mode = 'forest'">
          🌳 نمای شبکه کامل
        </button>
        <button class="tcla-mode-btn" :class="{ 'tcla-mode-btn--active': mode === 'ego' }" @click="mode = 'ego'">
          🔍 نمای یک مقاله
        </button>
        <button class="tcla-mode-btn" :class="{ 'tcla-mode-btn--active': mode === 'cluster' }" @click="mode = 'cluster'">
          📁 نمای خوشه
        </button>
      </div>

      <!-- Ego network controls -->
      <div v-if="mode === 'ego'" class="tcla-controls">
        <input
            v-model="searchQuery"
            type="text"
            placeholder="نام مقاله را جستجو کن..."
            class="tcla-search-input"
        >
        <div v-if="searchQuery && filteredOptions.length" class="tcla-search-results">
          <div
              v-for="opt in filteredOptions.slice(0, 8)"
              :key="opt.url"
              class="tcla-search-result"
              @click="selectArticle(opt.url)"
          >
            {{ opt.title }}
          </div>
        </div>
      </div>

      <!-- Cluster controls -->
      <div v-if="mode === 'cluster'" class="tcla-controls">
        <select v-model="selectedCluster" class="tcla-select">
          <option value="">— انتخاب خوشه —</option>
          <option v-for="c in topicalClusters" :key="c.name" :value="c.name">{{ c.name }} ({{ c.pageCount }} صفحه)</option>
        </select>
      </div>
    </div>

    <!-- ============================================================
         FOREST VIEW — every connected component as its own tree,
         stacked vertically. Cycle-safe (BFS with visited set).
         ============================================================ -->
    <div v-if="mode === 'forest'">
      <div class="tcla-panel">
        <div class="tcla-ego-legend">
          <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--focus"></i> ریشه (شروع درخت)</span>
          <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--spoke"></i> صفحه معمولی</span>
          <span class="tcla-legend-item"><span class="tcla-line-sample tcla-line-sample--back"></span> لینک برگشتی/حلقه (بدون گسترش دوباره)</span>
        </div>
        <div class="tcla-forest-stat">
          {{ forestLayout.trees.length }} شبکه‌ی به‌هم‌متصل شناسایی شد
          <span v-if="forestLayout.isolated.length"> · {{ forestLayout.isolated.length }} صفحه کاملاً منزوی</span>
        </div>
      </div>

      <div v-for="(tree, ti) in visibleTrees" :key="'tree'+ti" class="tcla-panel">
        <h3 class="tcla-panel__title">🌳 شبکه {{ ti + 1 }} — شروع از «{{ tree.rootTitle }}» ({{ tree.memberCount }} صفحه)</h3>
        <div class="tcla-svg-scroll">
          <svg :viewBox="'0 0 ' + tree.width + ' ' + tree.height" class="tcla-svg" :style="{ minWidth: tree.width + 'px' }">
            <line v-for="(e, i) in tree.edges" :key="'e'+i"
                  :x1="e.x1" :y1="e.y1" :x2="e.x2" :y2="e.y2"
                  class="tcla-edge tcla-edge--arrow" :class="{ 'tcla-edge--back': e.isBack }" />
            <g v-for="n in tree.nodes" :key="n.url"
               class="tcla-node-group"
               @click="switchToEgo(n.url)"
               @contextmenu.prevent="editArticle(n)"
            >
              <title>{{ n.fullTitle }}</title>
              <circle :cx="n.x" :cy="n.y" :r="n.r" :class="'tcla-node tcla-node--' + (n.isRoot ? 'focus' : 'spoke')" />
              <text :x="n.x" :y="n.y + n.r + 14" class="tcla-label">{{ n.shortTitle }}</text>
              <g v-if="n.source !== undefined" class="tcla-edit-btn" @click.stop="editArticle(n)">
                <title>ویرایش این مقاله</title>
                <circle :cx="n.x + n.r * 0.75" :cy="n.y - n.r * 0.75" r="9" class="tcla-edit-btn__bg" />
                <text :x="n.x + n.r * 0.75" :y="n.y - n.r * 0.75 + 3.5" class="tcla-edit-btn__icon">✎</text>
              </g>
            </g>
          </svg>
        </div>
      </div>

      <div v-if="forestLayout.trees.length > visibleTrees.length" class="tcla-panel">
        <button class="tcla-btn tcla-btn--link" @click="treeShowCount += 10">
          نمایش {{ Math.min(10, forestLayout.trees.length - visibleTrees.length) }} شبکه‌ی بعدی
          ({{ visibleTrees.length }} از {{ forestLayout.trees.length }})
        </button>
      </div>

      <div v-if="forestLayout.isolated.length" class="tcla-panel">
        <h3 class="tcla-panel__title">⚪ صفحات کاملاً منزوی (بدون هیچ لینک ورودی یا خروجی)</h3>
        <div class="tcla-isolated-list">
          <div v-for="n in forestLayout.isolated" :key="n.url" class="tcla-isolated-chip">
            <span class="tcla-dot tcla-dot--isolated"></span>
            {{ n.title }}
            <button class="tcla-btn tcla-btn--tiny" @click="editArticle(n)">✎</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Ego network view -->
    <div v-if="mode === 'ego' && focusNode" class="tcla-panel">
      <h3 class="tcla-panel__title">🕸️ نقشه لینک‌های «{{ focusNode.title }}»</h3>
      <div class="tcla-ego-legend">
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--in"></i> لینک ورودی (این‌ها به مقاله لینک داده‌اند)</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--focus"></i> مقاله انتخاب‌شده</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--out"></i> لینک خروجی (مقاله به این‌ها لینک داده)</span>
      </div>
      <svg :viewBox="'0 0 ' + egoLayout.width + ' ' + egoLayout.height" class="tcla-svg">
        <line v-for="(e, i) in egoLayout.edges" :key="'e'+i"
              :x1="e.x1" :y1="e.y1" :x2="e.x2" :y2="e.y2" class="tcla-edge" />
        <g v-for="n in egoLayout.nodes" :key="n.url"
           class="tcla-node-group"
           @click="n.type !== 'focus' && selectArticle(n.url)"
           @contextmenu.prevent="editArticle(n)"
        >
          <title>{{ n.fullTitle }}</title>
          <circle :cx="n.x" :cy="n.y" :r="n.r" :class="'tcla-node tcla-node--' + n.type" />
          <text :x="n.x" :y="n.y + n.r + 14" class="tcla-label">{{ n.shortTitle }}</text>
          <g v-if="n.source !== undefined" class="tcla-edit-btn" @click.stop="editArticle(n)">
            <title>ویرایش این مقاله</title>
            <circle :cx="n.x + n.r * 0.75" :cy="n.y - n.r * 0.75" r="9" class="tcla-edit-btn__bg" />
            <text :x="n.x + n.r * 0.75" :y="n.y - n.r * 0.75 + 3.5" class="tcla-edit-btn__icon">✎</text>
          </g>
        </g>
      </svg>
      <div v-if="egoOverflow.incoming || egoOverflow.outgoing" class="tcla-overflow-note">
        <span v-if="egoOverflow.incoming">+{{ egoOverflow.incoming }} لینک ورودی دیگر</span>
        <span v-if="egoOverflow.outgoing">+{{ egoOverflow.outgoing }} لینک خروجی دیگر</span>
      </div>
    </div>
    <div v-else-if="mode === 'ego'" class="tcla-panel">
      <div class="tcla-empty">یک مقاله را از جستجوی بالا انتخاب کن</div>
    </div>

    <!-- Cluster view -->
    <div v-if="mode === 'cluster' && clusterLayout" class="tcla-panel">
      <h3 class="tcla-panel__title">🕸️ نقشه خوشه «{{ selectedCluster }}»</h3>
      <div class="tcla-ego-legend">
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--hub"></i> Hub</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--spoke"></i> صفحه معمولی</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--orphan"></i> یتیم</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--deadend"></i> بن‌بست</span>
      </div>
      <svg :viewBox="'0 0 ' + clusterLayout.width + ' ' + clusterLayout.height" class="tcla-svg">
        <line v-for="(e, i) in clusterLayout.edges" :key="'e'+i"
              :x1="e.x1" :y1="e.y1" :x2="e.x2" :y2="e.y2"
              class="tcla-edge" :class="{ 'tcla-edge--missing': e.missing }" />
        <g v-for="n in clusterLayout.nodes" :key="n.url"
           class="tcla-node-group"
           @click="switchToEgo(n.url)"
           @contextmenu.prevent="editArticle(n)"
        >
          <title>{{ n.fullTitle }}</title>
          <circle :cx="n.x" :cy="n.y" :r="n.r" :class="'tcla-node tcla-node--' + n.type" />
          <text :x="n.x" :y="n.y + n.r + 14" class="tcla-label">{{ n.shortTitle }}</text>
          <g v-if="n.source !== undefined" class="tcla-edit-btn" @click.stop="editArticle(n)">
            <title>ویرایش این مقاله</title>
            <circle :cx="n.x + n.r * 0.75" :cy="n.y - n.r * 0.75" r="9" class="tcla-edit-btn__bg" />
            <text :x="n.x + n.r * 0.75" :y="n.y - n.r * 0.75 + 3.5" class="tcla-edit-btn__icon">✎</text>
          </g>
        </g>
      </svg>
    </div>
    <div v-else-if="mode === 'cluster'" class="tcla-panel">
      <div class="tcla-empty">یک خوشه را از بالا انتخاب کن</div>
    </div>
  </div>
</template>

<script>
const MAX_EGO_LINKS = 10; // per side, to keep the ego graph readable
const LEVEL_HEIGHT = 90;
const NODE_SPACING = 130;
const NODE_R = 16;

export default {
  name: 'TCLALinkGraph',
  props: {
    graph: { type: Object, default: () => ({ nodes: {} }) },
    topicalClusters: { type: Array, default: () => [] },
    hubs: { type: Array, default: () => [] }
  },
  data() {
    return {
      mode: 'forest',
      searchQuery: '',
      selectedArticleUrl: null,
      selectedCluster: '',
      treeShowCount: 10
    };
  },
  computed: {
    allOptions() {
      return Object.values(this.graph.nodes).map(n => ({ url: n.url, title: n.title }));
    },
    filteredOptions() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return [];
      return this.allOptions.filter(o => o.title.toLowerCase().includes(q));
    },
    focusNode() {
      if (!this.selectedArticleUrl) return null;
      return this.graph.nodes[this.selectedArticleUrl] || null;
    },
    egoOverflow() {
      if (!this.focusNode) return { incoming: 0, outgoing: 0 };
      return {
        incoming: Math.max(0, this.focusNode.incomingLinks.length - MAX_EGO_LINKS),
        outgoing: Math.max(0, this.focusNode.outgoingLinks.length - MAX_EGO_LINKS)
      };
    },
    egoLayout() {
      if (!this.focusNode) return { width: 800, height: 400, nodes: [], edges: [] };
      const width = 800;
      const incoming = this.focusNode.incomingLinks.slice(0, MAX_EGO_LINKS);
      const outgoing = this.focusNode.outgoingLinks.slice(0, MAX_EGO_LINKS);
      const rows = Math.max(incoming.length, outgoing.length, 1);
      const height = Math.max(300, rows * 60 + 80);
      const cx = width / 2, cy = height / 2;
      const shortTitle = (t) => (t && t.length > 24 ? t.slice(0, 24) + '…' : (t || ''));

      const nodes = [{ url: this.focusNode.url, x: cx, y: cy, r: 24, type: 'focus', shortTitle: shortTitle(this.focusNode.title), fullTitle: this.focusNode.title, source: this.focusNode.source }];
      const edges = [];

      incoming.forEach((link, i) => {
        const srcNode = this.graph.nodes[link.url];
        const y = 60 + i * (height - 100) / Math.max(1, incoming.length - 1 || 1);
        const x = 120;
        const title = srcNode ? srcNode.title : link.url;
        nodes.push({ url: link.url, x, y, r: 14, type: 'in', shortTitle: shortTitle(title), fullTitle: title, source: srcNode ? srcNode.source : undefined });
        edges.push({ x1: x, y1: y, x2: cx, y2: cy });
      });

      outgoing.forEach((link, i) => {
        const tgtNode = this.graph.nodes[link.url];
        const y = 60 + i * (height - 100) / Math.max(1, outgoing.length - 1 || 1);
        const x = width - 120;
        const title = tgtNode ? tgtNode.title : link.url;
        nodes.push({ url: link.url, x, y, r: 14, type: 'out', shortTitle: shortTitle(title), fullTitle: title, source: tgtNode ? tgtNode.source : undefined });
        edges.push({ x1: cx, y1: cy, x2: x, y2: y });
      });

      return { width, height, nodes, edges };
    },
    clusterLayout() {
      if (!this.selectedCluster) return null;
      const cluster = this.topicalClusters.find(c => c.name === this.selectedCluster);
      if (!cluster) return null;
      const hub = this.hubs.find(h => h.cluster === this.selectedCluster);
      const others = cluster.pages.filter(u => !hub || u !== hub.url);

      const width = 700, height = Math.max(320, 120 + others.length * 8);
      const cx = width / 2, cy = height / 2, radius = Math.min(width, height) / 2 - 70;
      const shortTitle = (t) => (t && t.length > 20 ? t.slice(0, 20) + '…' : (t || ''));

      const nodes = [];
      const edges = [];

      if (hub) {
        const hubNode = this.graph.nodes[hub.url];
        nodes.push({ url: hub.url, x: cx, y: cy, r: 22, type: 'hub', shortTitle: shortTitle(hub.title), fullTitle: hub.title, source: hubNode.source });
      }

      others.forEach((url, i) => {
        const node = this.graph.nodes[url];
        if (!node) return;
        const angle = (2 * Math.PI * i) / Math.max(1, others.length);
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        let type = 'spoke';
        if (node.isOrphan) type = 'orphan';
        else if (node.isDeadEnd) type = 'deadend';
        nodes.push({ url, x, y, r: 13, type, shortTitle: shortTitle(node.title), fullTitle: node.title, source: node.source });

        if (hub) {
          const linked = node.outgoingLinks.some(l => l.url === hub.url) ||
              this.graph.nodes[hub.url].outgoingLinks.some(l => l.url === url);
          edges.push({ x1: cx, y1: cy, x2: x, y2: y, missing: !linked });
        }
      });

      return { width, height, nodes, edges };
    },

    /* =====================================================================
     * FOREST LAYOUT — every weakly-connected component as its own tree.
     * Cycle-safe: a global `treeVisited` set ensures each node is only
     * ever expanded (given children) once. If a real link points back to
     * an already-visited node (e.g. B->A after A->B), that edge is still
     * drawn (as a dashed "back edge") but A is NOT re-expanded — this is
     * exactly what prevents infinite loops on cyclic link structures.
     * =================================================================== */
    forestLayout() {
      const nodes = this.graph.nodes;
      const urls = Object.keys(nodes);
      const shortTitle = (t) => (t && t.length > 20 ? t.slice(0, 20) + '…' : (t || ''));

      const neighborsOf = (url) => {
        const n = nodes[url];
        const set = new Set();
        n.outgoingLinks.forEach(l => { if (nodes[l.url]) set.add(l.url); });
        n.incomingLinks.forEach(l => { if (nodes[l.url]) set.add(l.url); });
        return set;
      };

      // Step 1: find weakly-connected components (undirected BFS).
      const globalVisited = new Set();
      const componentsList = [];
      urls.forEach(startUrl => {
        if (globalVisited.has(startUrl)) return;
        const compSet = new Set([startUrl]);
        const queue = [startUrl];
        while (queue.length) {
          const cur = queue.shift();
          neighborsOf(cur).forEach(nb => {
            if (!compSet.has(nb)) { compSet.add(nb); queue.push(nb); }
          });
        }
        compSet.forEach(u => globalVisited.add(u));
        componentsList.push([...compSet]);
      });

      const isolated = [];
      const trees = [];

      componentsList.forEach(memberUrls => {
        if (memberUrls.length === 1) {
          const u = memberUrls[0];
          isolated.push({ url: u, title: nodes[u].title, source: nodes[u].source });
          return;
        }

        // Step 2: pick a natural root — the page whose keyword/title has
        // the FEWEST words (e.g. "طراحی سایت" before "طراحی سایت پزشکی"
        // before "طراحی سایت پزشکی در اصفهان"). This reveals the actual
        // topical hierarchy (broad → specific), which is far more useful
        // for auditing link structure than picking by raw incoming-link
        // count. Ties broken by fewest incoming links, then by whichever
        // was discovered first during component traversal.
        let root = memberUrls[0];
        let bestSpecificity = nodes[root].profile.keywordSpecificity;
        let bestIncoming = nodes[root].incomingLinks.length;
        for (let i = 1; i < memberUrls.length; i++) {
          const u = memberUrls[i];
          const spec = nodes[u].profile.keywordSpecificity;
          const inCount = nodes[u].incomingLinks.length;
          if (spec < bestSpecificity || (spec === bestSpecificity && inCount < bestIncoming)) {
            root = u; bestSpecificity = spec; bestIncoming = inCount;
          }
        }

        // Step 3: BFS from root (cycle-safe via treeVisited), building levels.
        const treeVisited = new Set([root]);
        const levels = [[root]];
        const treeEdges = []; // forward edges that define the tree shape
        let frontier = [root];
        while (frontier.length) {
          const nextFrontier = [];
          frontier.forEach(cur => {
            const curNode = nodes[cur];
            const conns = [];
            curNode.outgoingLinks.forEach(l => { if (nodes[l.url]) conns.push({ to: l.url, dir: 'out' }); });
            curNode.incomingLinks.forEach(l => { if (nodes[l.url]) conns.push({ to: l.url, dir: 'in' }); });
            conns.forEach(c => {
              if (!treeVisited.has(c.to)) {
                treeVisited.add(c.to);
                nextFrontier.push(c.to);
                treeEdges.push({
                  from: c.dir === 'out' ? cur : c.to,
                  to: c.dir === 'out' ? c.to : cur,
                  isBack: false
                });
              }
            });
          });
          if (nextFrontier.length) {
            nextFrontier.sort((a, b) => nodes[a].profile.keywordSpecificity - nodes[b].profile.keywordSpecificity);
            levels.push(nextFrontier);
          }
          frontier = nextFrontier;
        }

        // Step 4: any real directed link not already captured as a tree
        // edge is a "back/cross edge" — drawn, but never expanded further.
        const treeEdgeKeys = new Set(treeEdges.map(e => e.from + '=>' + e.to));
        const backEdges = [];
        memberUrls.forEach(u => {
          nodes[u].outgoingLinks.forEach(l => {
            if (!nodes[l.url]) return;
            const key = u + '=>' + l.url;
            if (!treeEdgeKeys.has(key)) {
              backEdges.push({ from: u, to: l.url, isBack: true });
              treeEdgeKeys.add(key);
            }
          });
        });

        // Step 5: layout — levels stacked top-to-bottom, nodes spread
        // horizontally within each level.
        const maxLevelWidth = Math.max(...levels.map(l => l.length));
        const width = Math.max(500, maxLevelWidth * NODE_SPACING + 100);
        const height = levels.length * LEVEL_HEIGHT + 80;

        const positions = {};
        levels.forEach((levelUrls, li) => {
          const y = 50 + li * LEVEL_HEIGHT;
          const totalWidth = levelUrls.length * NODE_SPACING;
          const startX = (width - totalWidth) / 2 + NODE_SPACING / 2;
          levelUrls.forEach((u, i) => {
            positions[u] = { x: startX + i * NODE_SPACING, y };
          });
        });

        const treeNodes = memberUrls
            .filter(u => positions[u]) // safety — should always be true
            .map(u => ({
              url: u,
              x: positions[u].x,
              y: positions[u].y,
              r: u === root ? 20 : NODE_R,
              isRoot: u === root,
              shortTitle: shortTitle(nodes[u].title),
              fullTitle: nodes[u].title,
              source: nodes[u].source
            }));

        const allEdges = [...treeEdges, ...backEdges].map(e => ({
          x1: positions[e.from].x, y1: positions[e.from].y,
          x2: positions[e.to].x, y2: positions[e.to].y,
          isBack: e.isBack
        }));

        trees.push({
          rootTitle: nodes[root].title,
          memberCount: memberUrls.length,
          width,
          height,
          nodes: treeNodes,
          edges: allEdges
        });
      });

      // Biggest / most significant networks first.
      trees.sort((a, b) => b.memberCount - a.memberCount);

      return { trees, isolated };
    },
    visibleTrees() {
      return this.forestLayout.trees.slice(0, this.treeShowCount);
    }
  },
  methods: {
    selectArticle(url) {
      this.selectedArticleUrl = url;
      this.searchQuery = '';
      this.mode = 'ego';
    },
    switchToEgo(url) {
      this.mode = 'ego';
      this.selectedArticleUrl = url;
    },
    editArticle(node) {
      this.$emit('edit-blog', node.source);
    }
  }
};
</script>

<style scoped>
.tcla-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 20px;
  margin-bottom: 16px;
}
.tcla-panel__title { font-size: 0.92rem; font-weight: 700; margin: 0 0 12px 0; }
.tcla-empty { text-align: center; padding: 30px; color: #9ca3af; }

.tcla-mode-switch { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.tcla-mode-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  border-radius: 0.375rem;
  font-size: 0.82rem;
  cursor: pointer;
}
.tcla-mode-btn--active { background: #3b82f6; color: #fff; border-color: #3b82f6; }

.tcla-controls { position: relative; }
.tcla-search-input, .tcla-select {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  font-family: inherit;
}
.tcla-search-results {
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  margin-top: 4px;
  z-index: 10;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
}
.tcla-search-result {
  padding: 9px 12px;
  font-size: 0.82rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
}
.tcla-search-result:hover { background: #f3f4f6; }

.tcla-ego-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 14px;
  font-size: 0.76rem;
  color: #6b7280;
}
.tcla-legend-item { display: inline-flex; align-items: center; gap: 6px; }
.tcla-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }
.tcla-dot--in { background: #16a34a; }
.tcla-dot--out { background: #3b82f6; }
.tcla-dot--focus { background: #f59e0b; }
.tcla-dot--hub { background: #3b82f6; }
.tcla-dot--spoke { background: #16a34a; }
.tcla-dot--orphan { background: #ef4444; }
.tcla-dot--deadend { background: #9ca3af; }
.tcla-dot--isolated { background: #9ca3af; }

.tcla-line-sample {
  display: inline-block;
  width: 18px;
  height: 0;
  border-top: 2px dashed #ef4444;
}

.tcla-forest-stat { font-size: 0.8rem; color: #4b5563; }

.tcla-svg-scroll { overflow-x: auto; }
.tcla-svg { width: 100%; height: auto; display: block; }

.tcla-edge { stroke: #d1d5db; stroke-width: 1.5; }
.tcla-edge--missing { stroke: #ef4444; stroke-dasharray: 4 3; }
.tcla-edge--back { stroke: #ef4444; stroke-dasharray: 5 4; stroke-width: 1.8; }

.tcla-node { stroke: #fff; stroke-width: 2; cursor: pointer; }
.tcla-node--focus { fill: #f59e0b; }
.tcla-node--in { fill: #16a34a; }
.tcla-node--out { fill: #3b82f6; }
.tcla-node--hub { fill: #3b82f6; }
.tcla-node--spoke { fill: #16a34a; }
.tcla-node--orphan { fill: #ef4444; }
.tcla-node--deadend { fill: #9ca3af; }

.tcla-node-group { cursor: pointer; }
.tcla-label { font-size: 10px; fill: #6b7280; text-anchor: middle; }

.tcla-edit-btn { cursor: pointer; }
.tcla-edit-btn__bg {
  fill: #fff;
  stroke: #3b82f6;
  stroke-width: 1.5;
  transition: fill 0.15s;
}
.tcla-edit-btn:hover .tcla-edit-btn__bg { fill: #3b82f6; }
.tcla-edit-btn__icon {
  font-size: 9px;
  text-anchor: middle;
  fill: #3b82f6;
  pointer-events: none;
}
.tcla-edit-btn:hover .tcla-edit-btn__icon { fill: #fff; }

.tcla-overflow-note {
  margin-top: 10px;
  font-size: 0.76rem;
  color: #9ca3af;
  display: flex;
  gap: 14px;
}

.tcla-isolated-list { display: flex; flex-wrap: wrap; gap: 8px; }
.tcla-isolated-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 100px;
  padding: 5px 10px 5px 6px;
  font-size: 0.78rem;
  color: #4b5563;
}

.tcla-btn {
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.78rem;
  cursor: pointer;
}
.tcla-btn:hover { background: #f3f4f6; }
.tcla-btn--tiny { padding: 2px 7px; font-size: 0.72rem; border-radius: 100px; }
.tcla-btn--link { background: transparent; border: none; color: #3b82f6; }
.tcla-btn--link:hover { text-decoration: underline; background: transparent; }
</style>